import type { PointOfSaleToAreaSimple } from '@/views/pointofsales/pointOfSalesTypes';
import {
  AreasService,
  AreaV1Dto,
  CreateAreaV1Dto,
  type CreatePointOfSaleToAreaV1Dto,
  PatchAreaV1Dto,
  type PatchPointOfSaleToAreaV1Dto,
  PointOfSaleToAreasService,
  type PointOfSaleToAreaV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { AreaSimple } from '../areaTypes';

export function useAreas() {
  const fetchArea = async (id: string) => {
    try {
      return await AreasService.areaControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchAreas = async () => {
    try {
      return await AreasService.areaControllerV1FindAllV1();
    } catch (err) {
      console.log(err);
    }
  };

  const fetchAreasByCustomerId = async (customerId: string) => {
    try {
      return await AreasService.areaControllerV1FindManyByCustomerIdV1(customerId);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSaleToAreaByAreaId = async (id: string) => {
    try {
      return await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1FindManyByAreaIdV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPointOfSaleToAreaById = async (id: string) => {
    try {
      return await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
    }
  };

  const createArea = async (data: CreateAreaV1Dto) => {
    try {
      await AreasService.areaControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const createPointOfSaleToArea = async (data: CreatePointOfSaleToAreaV1Dto) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1CreateV1(data);
      eventBus.emit('create');
    } catch (err) {
      console.log(err);
    }
  };

  const updateArea = async (id: string, data: PatchAreaV1Dto) => {
    try {
      await AreasService.areaControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const updatePointOfSaleToArea = async (id: string, data: PatchPointOfSaleToAreaV1Dto) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteArea = async (id: string) => {
    try {
      await AreasService.areaControllerV1RemoveOneV1(id);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deletePointOfSaleOnArea = async (posToAreaId: string) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1RemoveOneV1(posToAreaId);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteAreas = async (ids: string[]) => {
    try {
      await AreasService.areaControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  const deleteManyPointOfSaleOnArea = async (ids: string[]) => {
    try {
      await PointOfSaleToAreasService.pointOfSaleToAreaControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchArea,
    fetchAreas,
    fetchPointOfSaleToAreaByAreaId,
    fetchPointOfSaleToAreaById,
    fetchAreasByCustomerId,
    createArea,
    createPointOfSaleToArea,
    updateArea,
    updatePointOfSaleToArea,
    deleteArea,
    deletePointOfSaleOnArea,
    deleteAreas,
    deleteManyPointOfSaleOnArea,
  };
}

export function useAreasConverter() {
  const convertAreaDtoToAreaSimple = (item: AreaV1Dto): AreaSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, ...res } = item;
    return {
      ...res,
      validFrom: item.validFrom,
      validTo: item.validTo,
    };
  };

  const convertAreaDtoListToAreaSimpleList = (data: AreaV1Dto[]): AreaSimple[] => {
    return data.map((item) => {
      return convertAreaDtoToAreaSimple(item);
    });
  };

  const convertAreaSimpleToCreateAreaDto = (data: AreaSimple): CreateAreaV1Dto => {
    const { id: _id, version: _version, ...res } = data;
    return res;
  };

  const convertAreaSimpleToUpdateAreaDto = (data: AreaSimple): PatchAreaV1Dto => {
    const { id: _id, ...res } = data;
    return res;
  };

  const convertAreaToPointOfSaleDtoToPointOfSaleToAreaSimple = (
    data: PointOfSaleToAreaV1Dto,
  ): PointOfSaleToAreaSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, ...res } = data;
    return { ...res };
  };

  const convertPointOfSaleToAreaSimpleToCreateAreaToPointOfSaleDto = (
    data: PointOfSaleToAreaSimple,
  ): CreatePointOfSaleToAreaV1Dto => {
    const {
      id: _id,
      version: _version,
      areaName: _areaName,
      pointOfSaleName: _pointOfSaleName,
      ...res
    } = data;
    return { ...res };
  };

  const convertPointOfSaleToAreaSimpleToPatchAreaToPointOfSaleDto = (
    data: PointOfSaleToAreaSimple,
  ): PatchPointOfSaleToAreaV1Dto => {
    const { id: _id, areaName: _areaName, pointOfSaleName: _pointOfSaleName, ...res } = data;
    return { ...res };
  };

  return {
    convertAreaDtoToAreaSimple,
    convertAreaDtoListToAreaSimpleList,
    convertAreaSimpleToCreateAreaDto,
    convertAreaSimpleToUpdateAreaDto,
    convertAreaToPointOfSaleDtoToPointOfSaleToAreaSimple,
    convertPointOfSaleToAreaSimpleToCreateAreaToPointOfSaleDto,
    convertPointOfSaleToAreaSimpleToPatchAreaToPointOfSaleDto,
  };
}
